import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { selectChartShowThresholdColor } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import { FloatingPopupContentChartActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-chart-actions/floating-popup-content-chart-actions.component';
import { FloatingPopupContentExportCsvActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-export-csv-actions/floating-popup-content-export-csv-actions.component';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { NgClass } from '@angular/common';
import { FpLoggerNoiseAiPredictorStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-ai-predictor/services/fp-logger-noise-ai-predictor/fp-logger-noise-ai-predictor.store';
import { FpLoggerNoiseAiPredictorChartService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-ai-predictor/services/fp-logger-noise-ai-predictor-chart.service';
import { FpLoggerNoiseAiPredictorExportService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-ai-predictor/services/fp-logger-noise-ai-predictor-export.service';
import { FpLoggerNoiseAiPredictorActionType } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-ai-predictor/services/fp-logger-noise-ai-predictor/fp-logger-noise-ai-predictor.actions';
import { FloatingPopupSelectDateRangeActionComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-select-date-range-action/floating-popup-select-date-range-action.component';
import { IEnumFormControlConfig } from '@app/core/interfaces/form-control-config';
import { MonthsDateRangeType } from '@app/core/date-range/enums/months-date-range-type.enum';
import { FormControlConfigType } from '@app/core/enums/form-control-config-type';
import { monthsDateRangeTypeLocalize } from '@app/core/date-range/localize/months-date-range-type';
import { FloatingPopupBarChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-bar-chart-wrapper/floating-popup-bar-chart-wrapper.component';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-logger-noise-ai-predictor',
  standalone: true,
  imports: [
    ElementWidthClassDirective,
    FloatingPopupBarChartWrapperComponent,
    FloatingPopupContentChartActionsComponent,
    FloatingPopupContentExportCsvActionsComponent,
    FloatingPopupSelectDateRangeActionComponent,
    FloatingPopupBaseErrorMessageComponent,
    NgClass,
  ],
  templateUrl: './fp-logger-noise-ai-predictor.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'min-h-0 grow-1',
  },
})
export class FpLoggerNoiseAiPredictorComponent {
  protected readonly screenResolutionService = inject(ScreenResolutionService);
  public readonly currentTabStore = inject(FpLoggerNoiseAiPredictorStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly chartService = inject(FpLoggerNoiseAiPredictorChartService);
  protected readonly fpLoggerNoiseAiPredictorExportService = inject(FpLoggerNoiseAiPredictorExportService);
  protected readonly floatingPopupService = inject(FloatingPopupService);
  private readonly store = inject(Store);

  public popup = input<IFloatingPopupInstance>();

  private readonly chartShowThresholdColor = toSignal(
    this.store.select(selectChartShowThresholdColor).pipe(map(value => !!value)),
    { initialValue: false }
  );

  public isChartFullscreen = toSignal(this.currentTabStore.isChartFullscreenActive$, {
    initialValue: false,
  });

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly loggerDeployment = computed(() => this.popupMainData()?.deployment);

  public readonly chartPlugins = [this.chartService.getBottomCirclesPlugin()];

  public combinedChartData = computed(() => {
    const data = this.data() || [];
    const showThresholdColor = this.chartShowThresholdColor();

    const chartData = this.chartService.getDataForChart(data, { showThresholdColor });
    const chartOptions = this.chartService.generateOptions();

    return { chartData, chartOptions };
  });

  public rangeSelection: IEnumFormControlConfig<MonthsDateRangeType> = {
    id: `select-date-range-${this.popup()?.id}`,
    type: FormControlConfigType.Enum,
    values: [
      MonthsDateRangeType.One,
      MonthsDateRangeType.Three,
      MonthsDateRangeType.Six,
      MonthsDateRangeType.Twelve,
      MonthsDateRangeType.TwentyFor,
      MonthsDateRangeType.FullHistory,
    ],
    defaultValue: MonthsDateRangeType.Six,
    localize: monthsDateRangeTypeLocalize,
    getValue: () => this.currentTabStore.period$,
    valueChange: value => {
      this.currentTabStore.dispatch({
        type: FpLoggerNoiseAiPredictorActionType.UpdatePeriod,
        period: value,
      });
    },
  };

  public doExport() {
    const popupMainData = this.popupMainData();

    if (!popupMainData) {
      return;
    }

    const data = this.data() || [];

    this.fpLoggerNoiseAiPredictorExportService.exportCsv(popupMainData, data);
  }

  public toggleChartFullscreen() {
    this.currentTabStore.dispatch({
      type: FpLoggerNoiseAiPredictorActionType.ToggleChartFullscreen,
      synchronizeWithPopupId: this.popup()?.id || undefined,
    });
  }
}
